/** Dependencies */
import angular from 'angular';
import ngSanitize from 'angular-sanitize';
/** Shared receipt formatting utilities */
import {
  getAccountName,
  getReceiptDues
} from '../../util/receiptFormatting/receiptFormatting';
/** Styles */
import './receiptCard.scss';
/** Template */
import receiptCardTemplate from './receiptCard.template.html';

/***************************************************************************
 * RETURN_STATUS_CONFIG
 * Stable, shared {status, bgClass} badge configs for the return-status
 * banner - reused BY REFERENCE across digests instead of building a fresh
 * object literal every time the status is checked. Mirrors WriteReceipts.js's
 * mStatusConfig.returned/partiallyReturned (the same badge shown in WNR).
 *
 * This matters: a template expression that calls a function returning a new
 * object literal on every call is a classic AngularJS "$rootScope:infdig"
 * trap - even when the contents are identical digest to digest, the object
 * REFERENCE is new every time, so Angular's default reference-based dirty
 * checking never settles and the digest loop hits its 10-iteration limit.
 * That's what started happening once ReceiptHistory.js began rendering a
 * whole list of these cards at once via ng-repeat (one WriteReceipts.js
 * instance alone didn't have enough unstable watchers active simultaneously
 * to blow past the limit, so this went unnoticed there).
 ***************************************************************************/
const RETURN_STATUS_CONFIG = {
  returned: { status: 'RETURNED', bgClass: 'bg-danger' },
  partiallyReturned: { status: 'PARTIALLY RETURNED', bgClass: 'bg-warning' }
};

export default angular
  .module('csm.ui.receiptCard', [ngSanitize])
  .component('receiptCard', {
    bindings: {
      receipt: '<',
      /** Optional - if bound, return-receipt numbers in the GR-2375 "Returned
          on receipt(s)" list become clickable and call this with {rr}, one
          entry from receipt.returnReceipts. If not bound, the list is
          display-only, since this component doesn't own any receipt-loading
          state itself (see WriteReceipts.js's $ctrl.viewReturnReceipt for how
          WNR wires the same list up to actual navigation). */
      onViewReturnReceipt: '&?'
    },
    template: receiptCardTemplate,
    controller: ReceiptCardController
  }).name;

/*******************************************************************************
 * ReceiptCardController */
/**
 * Using the receipt object, it creates and renders a single receipt card with
 * formatted detailing and display that is easy for users to follow.
 *******************************************************************************/
/* @ngInject */ function ReceiptCardController($injector, $scope) {
  const $svcs = { $ctrl: this, $scope };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the controller object and its handlers.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  Object.assign($ctrl, {
    formatAccountName: $injector.invoke(formatAccountName, null, $svcs),
    formatPaymentMethod: $injector.invoke(formatPaymentMethod, null, $svcs),
    formatReceiptTitle: $injector.invoke(formatReceiptTitle, null, $svcs),
    formatSoldTo: $injector.invoke(formatSoldTo, null, $svcs),
    isReturn: $injector.invoke(isReturn, null, $svcs),
    setReceiptDues: $injector.invoke(setReceiptDues, null, $svcs),
    showAccountItemName: $injector.invoke(showAccountItemName, null, $svcs),
    showItemName: $injector.invoke(showItemName, null, $svcs),
    viewReturnReceipt: $injector.invoke(viewReturnReceipt, null, $svcs),
    $onChanges: $injector.invoke(onChanges, null, $svcs)
  });

  /*-------------------------------------------------------------------*/
  /* Angular calls $onChanges once automatically before the first      */
  /* digest once bindings are set, but call it here too so             */
  /* $ctrl.returnStatus is populated immediately regardless.            */
  /*-------------------------------------------------------------------*/
  $ctrl.$onChanges({});
}

/*******************************************************************************
 * formatAccountName */
/**
 * Returns the display name for an account-type receipt item.
 *
 * @param  receiptItem  Account-type receipt item.
 *******************************************************************************/
/* @ngInject */ function formatAccountName(SERVER) {
  return function (receiptItem) {
    return getAccountName(receiptItem, SERVER.BooksDBConstants);
  };
}

/*******************************************************************************
 * formatPaymentMethod */
/**
 * Returns a human-readable label for a payment item.
 *
 * @param  paymentItem  Payment item on the receipt.
 * @param  receipt  Receipt the payment belongs to.
 *******************************************************************************/
/* @ngInject */ function formatPaymentMethod(SERVER) {
  return function (paymentItem, receipt) {
    // Legacy payment sources. They use the receipt.source values to determine the payment type.
    switch (receipt.source) {
      case SERVER.BooksDBConstants.RS_REVTRAK:
        return 'RevTrak';
      case SERVER.BooksDBConstants.RS_SCHOOLPAY:
        return 'School Pay';
      default:
        break;
    }

    switch (paymentItem.type) {
      case SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_DEBIT:
        return 'Funds Debited';
      case SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_CREDIT:
        return 'Funds Credited';
      case SERVER.BooksDBConstants.LIT_JOURNAL_CREDIT:
        if (
          receipt.type === SERVER.BooksDBConstants.LET_RETURN_RECEIPT &&
          paymentItem.subType !== SERVER.BooksDBConstants.LIS_OFFLINE_RETURN
        )
          return 'Check Written';
        break;
      default:
        break;
    }

    switch (paymentItem.subType) {
      case SERVER.BooksDBConstants.LIS_CASH_RECEIVED:
      case SERVER.BooksDBConstants.LIS_CASH_RETURNED:
        return 'Cash Tendered';
      case SERVER.BooksDBConstants.LIS_CHECK_RECEIVED:
      case SERVER.BooksDBConstants.LIS_CHECK_RETURNED:
        return `Check #${paymentItem.number} - ${paymentItem.name}`;
      case SERVER.BooksDBConstants.LIS_CREDIT_CARD:
        return `Credit Card #${paymentItem.number}`;
      case SERVER.BooksDBConstants.LIS_DEBIT_CARD:
        return `Debit Card #${paymentItem.number}`;
      case SERVER.BooksDBConstants.LIS_WEBSTORE:
        return `Webstore Transaction ID ${paymentItem.number}`;
      case SERVER.BooksDBConstants.LIS_OFFLINE_RETURN:
        return 'Offline Return Card Record';
      default:
        return 'Other';
    }
  };
}

/*******************************************************************************
 * formatReceiptTitle */
/**
 * Builds the receipt header title:
 *   "Receipt"            — new/unsaved receipt (no number)
 *   "Receipt #X"         — saved receipt
 *   "Return Receipt #X"  — return receipt
 *******************************************************************************/
/* @ngInject */ function formatReceiptTitle($ctrl) {
  return function () {
    const receipt = $ctrl.receipt;

    if (!receipt) return 'Receipt';

    const prefix = $ctrl.isReturn(receipt.type) ? 'Return ' : '';
    const number = receipt.number > 0 ? ` #${receipt.number}` : '';

    return `${prefix}Receipt${number}`;
  };
}

/*******************************************************************************
 * isReturn */
/**
 * Returns true when the receipt is a return receipt.
 *
 * @param  type  Receipt type.
 *******************************************************************************/
/* @ngInject */ function isReturn(SERVER) {
  return function (type) {
    return type === SERVER.BooksDBConstants.LET_RETURN_RECEIPT;
  };
}

/*******************************************************************************
 * formatSoldTo */
/**
 * Returns "Last, First (studentID) (grade)" for the receipt header.
 *
 * @param  receipt  Receipt to format the buyer for.
 *******************************************************************************/
/* @ngInject */ function formatSoldTo() {
  return function (receipt) {
    if (!receipt || !receipt.receiptFrom) return '-';

    let soldTo = receipt.receiptFrom;
    if (receipt.studentID) soldTo += ` (${receipt.studentID})`;
    if (receipt.studentID && receipt.grade >= 0)
      soldTo += ` (${receipt.grade})`;

    return soldTo;
  };
}

/*******************************************************************************
 * setReceiptDues */
/**
 * Calculates and returns trusted HTML for balance due / change due /
 * balance reduced, or null when none applies.
 *
 * @param  receipt  Receipt to calculate the dues for.
 *******************************************************************************/
/* @ngInject */ function setReceiptDues($sanitize, $sce, SERVER, Tools) {
  return function (receipt) {
    if (!receipt || !receipt.paymentItems || !receipt.paymentItems.length)
      return null;

    const { balanceDue, changeDue, balanceReduced } = getReceiptDues(
      receipt,
      SERVER.BooksDBConstants
    );

    let dueInfo = null;

    // Only one of these can exist at a time
    if (balanceDue > 0) {
      dueInfo = { label: 'BALANCE DUE', amount: balanceDue };
    } else if (changeDue > 0) {
      dueInfo = { label: 'CHANGE DUE', amount: changeDue };
    } else if (balanceReduced > 0) {
      dueInfo = { label: 'BALANCE REDUCED', amount: balanceReduced };
    }

    if (dueInfo) {
      const formattedAmount = `$${Tools.formatMoney(dueInfo.amount)}`;
      const html = `<span>${dueInfo.label}:</span><span class="receipt-due-amount">${formattedAmount}</span>`;

      // Sanitize the HTML to prevent XSS attacks, then mark it as trusted for binding in the template
      const sanitizedHtml = $sanitize(html);
      return $sce.trustAsHtml(sanitizedHtml);
    }

    return null;
  };
}

/*******************************************************************************
 * onChanges */
/**
 * Component lifecycle hook - recomputes $ctrl.returnStatus (the
 * {status, bgClass} badge config for whether THIS receipt has itself been
 * returned/partially returned - GR-2375, all five return types;
 * dbo.ReceiptStatus.sql's Returned/PartiallyReturned already aggregate all
 * of them - null when neither applies) whenever the one-way-bound receipt
 * input changes, instead of recomputing it inline in the template on every
 * digest - see RETURN_STATUS_CONFIG's own comment for why that mattered.
 * Distinct from isReturn(), which only says "this receipt IS a return
 * receipt," not "this receipt HAS BEEN returned."
 *******************************************************************************/
/* @ngInject */ function onChanges($ctrl) {
  return function () {
    const receipt = $ctrl.receipt;

    $ctrl.returnStatus = !receipt
      ? null
      : receipt.returned
        ? RETURN_STATUS_CONFIG.returned
        : receipt.partiallyReturned
          ? RETURN_STATUS_CONFIG.partiallyReturned
          : null;
  };
}

/*******************************************************************************
 * viewReturnReceipt */
/**
 * Calls the optional onViewReturnReceipt binding, if the host wired one up,
 * with the clicked return-receipt entry. No-op otherwise.
 *
 * @param  rr  Clicked return receipt entry.
 *******************************************************************************/
/* @ngInject */ function viewReturnReceipt($ctrl) {
  return function (rr) {
    if ($ctrl.onViewReturnReceipt) $ctrl.onViewReturnReceipt({ rr });
  };
}

/*******************************************************************************
 * showAccountItemName */
/**
 * Returns true when an account item should show "For: <name>" - a receivable
 * payment (e.g. a Destiny fine payment) that names who it's for. Other
 * account items already show their name as the account name.
 *
 * @param  item  Account-type receipt item.
 *******************************************************************************/
/* @ngInject */ function showAccountItemName(SERVER) {
  return function (item) {
    const { LIT_RECEIVABLE_CREDIT, LIT_RECEIVABLE_DEBIT } =
      SERVER.BooksDBConstants;
    return (
      !!item.name &&
      (item.type === LIT_RECEIVABLE_CREDIT ||
        item.type === LIT_RECEIVABLE_DEBIT)
    );
  };
}

/*******************************************************************************
 * showItemName */
/**
 * Returns true when the "For: <name>" label should be shown on an item.
 * Hidden when its options carry their own "For" line. A kit shows its own
 * name here and its sub items show none, like ReceiptPDF.cs and WNR.
 *
 * @param  item  Receipt item.
 *******************************************************************************/
/* @ngInject */ function showItemName() {
  return function (item) {
    return item.name && (!item.options || item.options.length === 0);
  };
}
